import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { HomepageModuleTemplateId } from './homepageModuleTemplates';

interface AddModuleModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (tabName: string, sectionTitle: string, template?: HomepageModuleTemplateId) => void;
  editMode?: boolean;
  initialTabName?: string;
  initialSectionTitle?: string;
  onUpdate?: (tabName: string, sectionTitle: string) => void;
}

const AddModuleModal: React.FC<AddModuleModalProps> = ({ 
  open, 
  onClose, 
  onAdd, 
  editMode = false,
  initialTabName = '',
  initialSectionTitle = '',
  onUpdate
}) => {
  const { t } = useTranslation('salesSystem');
  const [tabName, setTabName] = useState('');
  const [sectionTitle, setSectionTitle] = useState('');
  const [template, setTemplate] = useState<HomepageModuleTemplateId>('default');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (editMode) {
        setTabName(initialTabName);
        setSectionTitle(initialSectionTitle);
      } else {
        setTabName('');
        setSectionTitle('');
        setTemplate('default');
      }
      setError('');
    }
  }, [open, editMode, initialTabName, initialSectionTitle]);

  const handleSubmit = () => {
    if (!tabName.trim()) {
      setError(t('ecommerceHomeSettings.moduleModal.tabNameRequired'));
      return;
    }
    setError('');
    const normalizedTabName = tabName.trim();
    const normalizedSectionTitle = sectionTitle.trim();
    
    if (editMode && onUpdate) {
      onUpdate(normalizedTabName, normalizedSectionTitle);
    } else {
      onAdd(normalizedTabName, normalizedSectionTitle, template);
    }
    
    onClose();
  };

  const templateCardSx = (active: boolean) => ({
    flex: 1,
    p: 1.5,
    borderRadius: 2,
    border: '2px solid',
    borderColor: active ? 'primary.main' : 'divider',
    bgcolor: active ? 'primary.50' : 'background.paper',
    cursor: 'pointer',
    textAlign: 'left' as const,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editMode
          ? t('ecommerceHomeSettings.moduleModal.editTitle')
          : t('ecommerceHomeSettings.moduleModal.addTitle')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            id="tabName"
            label={t('ecommerceHomeSettings.moduleModal.tabNameLabel')}
            type="text"
            fullWidth
            variant="outlined"
            value={tabName}
            onChange={(e) => setTabName(e.target.value)}
            helperText={t('ecommerceHomeSettings.moduleModal.tabNameHelper')}
          />
          <TextField
            margin="dense"
            id="sectionTitle"
            label={t('ecommerceHomeSettings.moduleModal.sectionTitleLabel')}
            type="text"
            fullWidth
            variant="outlined"
            value={sectionTitle}
            onChange={(e) => setSectionTitle(e.target.value)}
            helperText={t('ecommerceHomeSettings.moduleModal.sectionTitleHelper')}
          />
          {!editMode && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('ecommerceHomeSettings.moduleModal.templateLabel')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => setTemplate('default')}
                  onKeyDown={(e) => e.key === 'Enter' && setTemplate('default')}
                  sx={{ ...templateCardSx(template === 'default'), minWidth: 160 }}
                >
                  <Typography variant="body2" fontWeight="bold">
                    {t('ecommerceHomeSettings.moduleModal.templateDefaultTitle')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('ecommerceHomeSettings.moduleModal.templateDefaultHelp')}
                  </Typography>
                </Box>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => setTemplate('category-row')}
                  onKeyDown={(e) => e.key === 'Enter' && setTemplate('category-row')}
                  sx={{ ...templateCardSx(template === 'category-row'), minWidth: 160 }}
                >
                  <Typography variant="body2" fontWeight="bold">
                    {t('ecommerceHomeSettings.moduleModal.templateCategoryTitle')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('ecommerceHomeSettings.moduleModal.templateCategoryHelp')}
                  </Typography>
                </Box>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => setTemplate('brand-flow')}
                  onKeyDown={(e) => e.key === 'Enter' && setTemplate('brand-flow')}
                  sx={{ ...templateCardSx(template === 'brand-flow'), minWidth: 160 }}
                >
                  <Typography variant="body2" fontWeight="bold">
                    {t('ecommerceHomeSettings.moduleModal.templateBrandFlowTitle')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('ecommerceHomeSettings.moduleModal.templateBrandFlowHelp')}
                  </Typography>
                </Box>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => setTemplate('showcase')}
                  onKeyDown={(e) => e.key === 'Enter' && setTemplate('showcase')}
                  sx={{ ...templateCardSx(template === 'showcase'), minWidth: 160 }}
                >
                  <Typography variant="body2" fontWeight="bold">
                    {t('ecommerceHomeSettings.moduleModal.templateShowcaseTitle')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('ecommerceHomeSettings.moduleModal.templateShowcaseHelp')}
                  </Typography>
                </Box>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => setTemplate('showcase-banner')}
                  onKeyDown={(e) => e.key === 'Enter' && setTemplate('showcase-banner')}
                  sx={{ ...templateCardSx(template === 'showcase-banner'), minWidth: 160 }}
                >
                  <Typography variant="body2" fontWeight="bold">
                    {t('ecommerceHomeSettings.moduleModal.templateShowcaseBannerTitle')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('ecommerceHomeSettings.moduleModal.templateShowcaseBannerHelp')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          {error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('ecommerceHomeSettings.actions.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained">
          {editMode
            ? t('ecommerceHomeSettings.moduleModal.update')
            : t('ecommerceHomeSettings.moduleModal.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddModuleModal;
